import { useState, useEffect } from 'react'
import { Eye, EyeOff, Moon, Sun } from 'lucide-react'
import { supabase } from '../services/supabase'

export function Signup() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('sfp_dark_mode') === 'true'
  })

  const toggleDarkMode = () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)
    localStorage.setItem('sfp_dark_mode', newMode ? 'true' : 'false')
  }

  // Extract email from magic link hash (from Supabase recovery flow)
  useEffect(() => {
    const hash = window.location.hash
    if (hash) {
      const params = new URLSearchParams(hash.substring(1))
      params.get('type')

      // If coming from a magic link, Supabase sets a session
      // We can use the current user's email
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user?.email) {
          setEmail(session.user.email)
        }
      })
    }
  }, [])

  // Calculate password strength
  const calculatePasswordStrength = (pass) => {
    let strength = 0
    if (pass.length >= 8) strength += 1
    if (/[A-Z]/.test(pass)) strength += 1
    if (/[a-z]/.test(pass)) strength += 1
    if (/[0-9]/.test(pass)) strength += 1
    if (/[^A-Za-z0-9]/.test(pass)) strength += 1
    return strength
  }

  const handlePasswordChange = (e) => {
    const pass = e.target.value
    setPassword(pass)
    setPasswordStrength(calculatePasswordStrength(pass))
  }

  const handleSignUp = async (e) => {
    e.preventDefault()
    setError('')
    setMessage('')

    // Validation
    if (!email) {
      setError('Email is required')
      return
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (passwordStrength < 3) {
      setError('Password must contain uppercase, lowercase, and numbers')
      return
    }

    setLoading(true)
    try {
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      })

      if (updateError) throw updateError

      setMessage('Account created successfully! Signing you in...')

      // Redirect to dashboard after brief delay
      setTimeout(() => {
        window.location.href = '/'
      }, 1500)
    } catch (err) {
      setError(err.message || 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  const getPasswordStrengthColor = () => {
    if (passwordStrength === 0) return '#CCCCCC'
    if (passwordStrength <= 2) return '#ef4444'
    if (passwordStrength === 3) return '#f59e0b'
    return '#10b981'
  }

  const getPasswordStrengthText = () => {
    if (password.length === 0) return ''
    if (passwordStrength <= 2) return 'Weak'
    if (passwordStrength === 3) return 'Fair'
    return 'Strong'
  }

  return (
    <div className={`min-h-screen flex relative transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
    }`}>

      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-6 right-6 z-50 p-2.5 rounded-full transition-all duration-200"
        style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(245,166,35,0.1)',
          color: isDarkMode ? '#fff' : '#F5A623',
        }}
        title={isDarkMode ? 'Light mode' : 'Dark mode'}
      >
        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      {/* ── LEFT: Brand panel ───────────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-[55%] flex-col items-center justify-center relative overflow-hidden p-16 transition-all duration-300"
        style={{
          background: isDarkMode
            ? 'linear-gradient(150deg, #1e293b 0%, #0f172a 55%, #020617 100%)'
            : 'linear-gradient(150deg, #ffffff 0%, #fdf8f0 55%, #fef9f2 100%)'
        }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(245,166,35,0.15) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Top-left ambient glow */}
        <div className="absolute pointer-events-none" style={{
          top: '-20%', left: '-20%',
          width: '70%', height: '70%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.10) 0%, transparent 65%)',
        }} />

        {/* Bottom-right ambient glow */}
        <div className="absolute pointer-events-none" style={{
          bottom: '-20%', right: '-15%',
          width: '60%', height: '60%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.07) 0%, transparent 65%)',
        }} />

        {/* Content */}
        <div className="relative z-10 w-full max-w-lg">

          {/* Logo */}
          <img
            src={isDarkMode ? '/SFP Wealth Logo - For Digital.png' : '/dark logo SFP.JPG'}
            alt="SFP Wealth"
            className="mb-14"
            style={{ width: '200px' }}
          />

          {/* Headline */}
          <h1 style={{
            fontSize: '2.25rem',
            fontWeight: 700,
            color: isDarkMode ? '#ffffff' : '#333333',
            lineHeight: '1.15',
            letterSpacing: '-0.03em',
            marginBottom: '1rem',
          }}>
            Wealth Management,<br />
            <span style={{ color: '#F5A623' }}>Simplified.</span>
          </h1>

          <p style={{
            color: isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(51,51,51,0.50)',
            fontSize: '1rem',
            lineHeight: '1.7',
            maxWidth: '380px',
            marginBottom: '3rem',
          }}>
            Your complete platform for client relationships, portfolio oversight, and financial planning — all in one place.
          </p>

          {/* Feature bullets */}
          <div className="space-y-4">
            {[
              { dot: '#F5A623', text: 'Centralised client and policy management with real-time sync' },
              { dot: '#10b981', text: 'Claims tracking integrated directly with the Dispatch portal' },
              { dot: '#60a5fa', text: 'Role-based access control and full advisor permissions' },
            ].map((item) => (
              <div key={item.text} className="flex items-start gap-3">
                <div className="mt-2 shrink-0 rounded-full" style={{
                  width: 7, height: 7,
                  backgroundColor: item.dot,
                }} />
                <p style={{
                  color: isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(51,51,51,0.50)',
                  fontSize: '0.875rem',
                  lineHeight: '1.6',
                }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom badge */}
        <div className="absolute bottom-8 left-16 flex items-center gap-2" style={{
          color: isDarkMode ? 'rgba(245,166,35,0.40)' : 'rgba(245,166,35,0.55)',
          fontSize: '0.7rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}>
          <div className="rounded-full" style={{ width: 6, height: 6, backgroundColor: '#F5A623', opacity: isDarkMode ? 0.4 : 0.6 }} />
          SFP Wealth CRM
        </div>
      </div>

      {/* ── RIGHT: Form panel ───────────────────────────────────── */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-8 py-12 transition-colors duration-300"
        style={{ backgroundColor: isDarkMode ? '#0f172a' : '#F5F5F5' }}
      >
        {/* Mobile logo */}
        <div className="lg:hidden mb-10 text-center">
          <img src={isDarkMode ? '/SFP Wealth Logo - For Digital.png' : '/dark logo SFP.JPG'} alt="SFP Wealth" className="h-12 w-auto mx-auto" />
        </div>

        <div className="w-full max-w-sm">

          {/* Heading */}
          <div className="mb-8">
            <h2 style={{
              fontSize: '1.625rem',
              fontWeight: 700,
              color: isDarkMode ? '#ffffff' : '#333333',
              letterSpacing: '-0.02em',
              marginBottom: '0.375rem',
            }}>
              Create your account
            </h2>
            <p style={{ color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(51,51,51,0.45)', fontSize: '0.875rem' }}>
              Set up your password and get started with SFP Wealth
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-xl px-4 py-3 mb-5 text-sm" style={{
              backgroundColor: isDarkMode ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)',
              border: isDarkMode ? '1px solid rgba(239,68,68,0.40)' : '1px solid rgba(239,68,68,0.20)',
              color: isDarkMode ? '#ff6b6b' : '#dc2626',
            }}>
              {error}
            </div>
          )}

          {/* Success */}
          {message && (
            <div className="rounded-xl px-4 py-3 mb-5 text-sm" style={{
              backgroundColor: isDarkMode ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.08)',
              border: isDarkMode ? '1px solid rgba(16,185,129,0.40)' : '1px solid rgba(16,185,129,0.20)',
              color: isDarkMode ? '#5eead4' : '#059669',
            }}>
              {message}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSignUp} className="space-y-4">

            {/* Email (read-only if from magic link) */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}>
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@sfpwealth.co.za"
                required
                className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: email && !loading ? (isDarkMode ? '#0f172a' : '#f5f5f5') : (isDarkMode ? '#1e293b' : '#ffffff'),
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #CCCCCC',
                  color: isDarkMode ? '#ffffff' : '#333333',
                  cursor: email && !loading ? 'not-allowed' : 'text',
                }}
                disabled={!!email && !loading}
                onFocus={(e) => { if (!email || loading) e.target.style.borderColor = 'rgba(245,166,35,0.70)' }}
                onBlur={(e) => { e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#CCCCCC' }}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="••••••••"
                  required
                  className="w-full rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none transition-colors duration-200"
                  style={{
                    backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #CCCCCC',
                    color: isDarkMode ? '#ffffff' : '#333333',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(245,166,35,0.70)' }}
                  onBlur={(e) => { e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#CCCCCC' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(51,51,51,0.65)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {password && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    height: '4px',
                    flex: 1,
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(passwordStrength / 5) * 100}%`,
                      backgroundColor: getPasswordStrengthColor(),
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <span style={{ color: getPasswordStrengthColor() }}>
                    {getPasswordStrengthText()}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}>
                Confirm password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none transition-colors duration-200"
                  style={{
                    backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #CCCCCC',
                    color: isDarkMode ? '#ffffff' : '#333333',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(245,166,35,0.70)' }}
                  onBlur={(e) => { e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#CCCCCC' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(51,51,51,0.65)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#F5A623',
                color: '#ffffff',
                marginTop: '0.75rem',
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#d4891a' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F5A623' }}
            >
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

          {/* Back to login */}
          <button
            type="button"
            onClick={() => window.location.href = '/'}
            className="w-full text-center text-sm mt-4 transition-colors"
            style={{ color: isDarkMode ? 'rgba(255,255,255,0.40)' : 'rgba(51,51,51,0.40)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.40)' : 'rgba(51,51,51,0.40)' }}
          >
            ← Back to login
          </button>
        </div>

        {/* Footer */}
        <p className="absolute bottom-6 text-center text-xs" style={{ color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(51,51,51,0.25)' }}>
          © {new Date().getFullYear()} SFP Wealth. All rights reserved.
        </p>
      </div>
    </div>
  )
}
